import { WorkModel } from '@Entities/work/work'
import React, { useContext, useEffect, useState } from 'react'
import { PaginationBody } from '@Entities/pagination'
import RecordingsTable from '@Components/ViewWork/RecordingsTable'
import { getDspRecordingsForSongCode, getPubRecordingsForSongCode } from '@App/api/routes/recording'
import PointPreloader from '@Components/Preloaders/PointPreloader'
import ViewWorkSection from '@Components/ViewWork/ViewWorkSection'
import axios from 'axios'
import { mapRecordingToRecordingModel } from '@App/helpers/recording-to-recordingmodel'
import { RecordingModel } from '@Entities/work/recording-model'
import { observer } from 'mobx-react'
import { StoresContext } from '@App/contexts'
import Button from '@Components/Buttons/Button'
import './index.scss'

interface MatchedRecordingsSectionProps {
    work: WorkModel
    matchOnClick?: () => void
}

const PAGE_LIMIT = 10

const MatchedRecordingsSection = (props: MatchedRecordingsSectionProps) => {
    const { authStore } = useContext(StoresContext)

    const [loading, setLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalRecordings, setTotalRecordings] = useState(0)
    const [recordingPage, setRecordingPage] = useState<RecordingModel[]>([])

    const { work, matchOnClick } = props

    const handlePage = (pagination: PaginationBody) => {
        setLoading(true)
        const newPage = pagination.page ?? 1
        setCurrentPage(newPage)
        if (authStore.isLoggedIn) {
            getDspRecordingsForSongCode(work.songCode ?? '', pagination)
                .then((axiosResult) => {
                    setTotalRecordings(axiosResult.data.count)
                    setRecordingPage(axiosResult.data.recordings.map(mapRecordingToRecordingModel))
                })
                .catch((error) => {
                    if (axios.isAxiosError(error)) {
                        if ('Error: No recordings match that song code.' !== error.message) {
                            throw error
                        }
                    }
                })
                .finally(() => setLoading(false))
        } else {
            getPubRecordingsForSongCode(work.songCode ?? '', pagination)
                .then((axiosResult) => {
                    setTotalRecordings(axiosResult.data.totalElements)
                    setRecordingPage(axiosResult.data.content.map(mapRecordingToRecordingModel))
                })
                .catch((error) => {
                    if (axios.isAxiosError(error)) {
                        if ('Error: No recordings match that song code.' !== error.message) {
                            throw error
                        }
                    }
                })
                .finally(() => setLoading(false))
        }
    }

    useEffect(() => {
        handlePage({ page: 1, limit: PAGE_LIMIT })
    }, [authStore.isLoggedIn, work.songCode])

    return (
        <ViewWorkSection icon="recordings" title="Matched Recordings" count={totalRecordings}>
            {loading ? (
                <PointPreloader />
            ) : totalRecordings === 0 ? (
                <div className="matched-recordings-empty">
                    <p className="matched-recordings-empty-title">You have no matched recordings</p>
                    {matchOnClick && (
                        <>
                            <p className="matched-recordings-empty-subtitle">
                                Click &quot;Match Works&quot; to match unmatched recordings.
                            </p>
                            <Button mainColor type="primary" onClick={matchOnClick}>
                                Match Works
                            </Button>
                        </>
                    )}
                </div>
            ) : (
                <RecordingsTable
                    recordings={recordingPage}
                    totalRecordings={totalRecordings}
                    currentPage={currentPage}
                    handlePage={handlePage}
                    recordingType="matched"
                />
            )}
        </ViewWorkSection>
    )
}

export default observer(MatchedRecordingsSection)
